import { Dispatch, SetStateAction, useCallback, useEffect } from "react";
import { NodeConnection, NodeExecutionCondition, WorkflowNode } from "../../types/workflow";
import customFetch from "../../utils/customFetch";
import { toast } from "react-toastify";
import { useWorkflowId } from "../../utils/misc";
import { useGetNodeConnectionsByWorkflowId, useGetNodesByWorkflowId } from "../../api/nodeApi";
import { Node } from "../../types/workflow";

const getNodeOrder = (nodes: Partial<Node>[]) => {
    const order = nodes.sort((a, b) => {
        const createdTimeA = new Date(a.created_time || '').getTime();
        const createdTimeB = new Date(b.created_time || '').getTime();
        return createdTimeA - createdTimeB
     }).map((node) => node.id || '')

    return order;
};

const useFetchWorkflowNodes = ({
    setWorkflowNodes,
    setExecutionConditionsForNodes,
    fetchNodeConnections,
}: {
    setWorkflowNodes: (workflowNodes: WorkflowNode[]) => void,
    setExecutionConditionsForNodes: Dispatch<SetStateAction<Record<string, NodeExecutionCondition[]>>>,
    fetchNodeConnections: () => void,
}) => {
    const workflowId = useWorkflowId();
    const { mutate: getNodeConnectionsByWorkflowId } = useGetNodeConnectionsByWorkflowId();
    const { mutate: getNodesForWorkflow } = useGetNodesByWorkflowId();

    const updateWorkflowNodes = useCallback(async (nodeConnections: NodeConnection[], nodeIdToWorkflowNodeMap: Record<string, WorkflowNode>) => {
        const order = getNodeOrder(Object.values(nodeIdToWorkflowNodeMap)?.map(node => node.node));
        const promises = nodeConnections.map(connection => {
            return customFetch(`/v1/nodeexecutionconditions?nodeconnection=${connection.id}`)
                .then(async(res) => {
                    const data = await res.json();
                    return res.ok ? data : null;
                }).then((executionConditions) => {
                    setExecutionConditionsForNodes((prev) => {
                        let executionConditionsForNode = executionConditions.map((condition: NodeExecutionCondition) => ({
                            ...condition,
                            node_connection: connection,
                        }));

                        if (prev[connection.end_node.id]) {
                            // Filter out conditions from the same connection to avoid duplicates
                            const conditionsFromOtherConnections = prev[connection.end_node.id].filter(
                                (condition: NodeExecutionCondition) => condition.node_connection?.id !== connection.id
                            );
                            executionConditionsForNode = [
                                ...conditionsFromOtherConnections,
                                ...executionConditionsForNode,
                            ];
                        }

                        return {
                            ...prev,
                            [connection.end_node.id]: executionConditionsForNode,
                        }
                    });    
                    nodeIdToWorkflowNodeMap[connection.end_node.id] = {
                        ...nodeIdToWorkflowNodeMap[connection.end_node.id],
                        nodeConnection: {...connection},
                    };
                })
        });
    
        try {
            await Promise.allSettled(promises);
            setWorkflowNodes(order.map(o => nodeIdToWorkflowNodeMap[o]));
        } catch (error) {
            toast.error("Error getting workflow nodes");
        }
    }, [setExecutionConditionsForNodes, setWorkflowNodes]);

    const refreshWorkflowNodes = useCallback(() => {
        getNodeConnectionsByWorkflowId({workflowId: workflowId}, {
            onSuccess: (nodeConnections) => {
                getNodesForWorkflow({ workflowId: workflowId }, {
                    onSuccess: (nodes) => {
                        let nodeIdToWorkflowNodeMap: Record<string, WorkflowNode> = {};
                        nodes.forEach(node => nodeIdToWorkflowNodeMap[node.id] = { node: node });
                        if(nodeConnections?.length && Array.isArray(nodeConnections)) {
                            updateWorkflowNodes(nodeConnections, nodeIdToWorkflowNodeMap);
                        } else {
                            setWorkflowNodes(nodes.map(node => ({node: node})));
                        }
                    }
                });
            }
        });
        fetchNodeConnections();
    }, [workflowId, getNodeConnectionsByWorkflowId, getNodesForWorkflow, updateWorkflowNodes, fetchNodeConnections, setWorkflowNodes]);
    useEffect(() => {
        refreshWorkflowNodes();
    }, [refreshWorkflowNodes]);

    return {
        refreshWorkflowNodes,
    }
}

export default useFetchWorkflowNodes;